import React, { useMemo, useState, useEffect } from 'react';
import { Button, Input, Label } from 'reactstrap';
import { useTranslation, Trans } from 'react-i18next';
import { createChangeHandler, CreateEnterCallback } from 'services/formUtils';
import { ResetTwoFactorAuthenticationModal, SubmitButton } from 'components';
import './CustomLogin.css';
import { QRCode } from 'react-qr-code';

const OneUseCodeLogin = (props: ISetPasswordProps) => {
  const { t } = useTranslation('translation');
  const [code, setCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState(false);

  const onSubmit = () => {
    if (!canBeSubmitted) {
      return;
    }

    setIsSubmitting(true);

    props
      .onSubmit(code)
      .then(
        () => {
          setIsSubmitting(false);
        },
        () => {
          setIsSubmitting(false);
        }
      )
      .catch(() => {
        setIsSubmitting(false);
      });
  };

  const canBeSubmitted = useMemo(() => {
    return code.length == 6 && !isSubmitting;
  }, [isSubmitting, code]);

  return (
    <>
      {props.qrRegistrationUrl ? (
        <>
          <div style={{ display: 'flex', flexDirection: 'row' }}>
            <div style={{ display: 'block', marginBottom: '2em' }}>
              <p style={{ paddingLeft: '0', paddingRight: '0' }}>
                {t('login.REASON_FOR_AUTHENTICATOR')}
              </p>
              <p style={{ paddingLeft: '0', paddingRight: '0' }}>
                <span style={{ fontWeight: 'bold' }}>
                  {t('login.AUTHENTICATOR_MISSING_APP_TITLE')}
                </span>
                <div> {t('login.AUTHENTICATOR_MISSING_APP_INSTRUCTIONS')}</div>
              </p>
              <span style={{ fontWeight: 'bold' }}>
                {t('login.AUTHENTICATOR_REGISTRATION_TITLE')}
              </span>
              <Trans i18nKey="login.AUTHENTICATOR_REGISTRATION_INSTRUCTIONS" ns="translation">
                <div>
                  <ol>
                    <li></li>
                    <li></li>
                    <li></li>
                    <li></li>
                    <li></li>
                  </ol>
                </div>
              </Trans>
              <div style={{ fontWeight: 'bold' }}>
                {t('login.AUTHENTICATOR_REGISTRATION_LINKS_TITLE')}
              </div>
              <div>{t('login.AUTHENTICATOR_REGISTRATION_DESCRIPTION')}</div>
              <br />
              <a target="_blank" href="https://info.avonova.se/sv/portal/guide-mfa">
                {t('login.AUTHENTICATOR_REGISTRATION_LINKS')}
              </a>
            </div>
            <div style={{ display: 'block' }}>
              <QRCode
                size={256}
                style={{ height: '256', width: '256', padding: '1rem' }}
                value={props.qrRegistrationUrl}
                viewBox={`0 0 256 256`}
              />
            </div>
          </div>
        </>
      ) : (
        <>
          <h3 style={{ marginTop: '0em' }}>{t('login.TOTP')}</h3>
          <Label style={{ paddingBottom: '15px' }}>{t('login.AUTHENTICATOR_APP_USE')} </Label>
        </>
      )}
      <Input
        placeholder={t('login.PROMPT_FOR_ONE_USE_CODE')}
        type="text"
        value={code}
        onChange={createChangeHandler(setCode)}
        onKeyPress={CreateEnterCallback(() => onSubmit())}
      />
      <span className="buttons">
        {props.onRestart && (
          <Button onClick={props.onRestart}>{t('login.SUBMIT_BUTTON_RESTART')}</Button>
        )}
        {!props.qrRegistrationUrl && (
          <Button
            onClick={() => {
              setIsOpenModal(true);
            }}
          >
            {t('login.SUBMIT_BUTTON_RESETTING_TWO_FACTOR_AUTHENTICATION')}
          </Button>
        )}
        <SubmitButton
          submitting={isSubmitting ? true : undefined}
          disabled={!canBeSubmitted}
          className="btn-primary"
          activelabel={t('login.SUBMIT_BUTTON_SUBMITTING')}
          label={t('login.SUBMIT_BUTTON_SUBMIT')}
          onClick={onSubmit}
        />
      </span>
      <ResetTwoFactorAuthenticationModal
        open={isOpenModal}
        onClose={() => {
          setIsOpenModal(false);
        }}
      />
    </>
  );
};

export interface ISetPasswordProps {
  onSubmit: (oneUseCode: string) => Promise<void>;
  onRestart?: () => void;
  qrRegistrationUrl?: string;
}

export default OneUseCodeLogin;
